import { v4 as uuidv4 } from 'uuid';
import { produce } from 'immer';

import Choice from 'types/Choice';
import Field from 'types/Field';
import FieldDataType from 'types/FieldDataType';
import FieldType from 'types/FieldType';
import UnitType from 'types/UnitType';
import MeasurementType from 'types/MeasurementType';
import Section from 'types/Section';
import { NewTemplate } from 'types/Template';
import TemplateType from 'types/TemplateType';

export type Action =
  | { type: 'set-template-name'; name: string }
  | { type: 'set-template-reference-label'; referenceLabel: string }
  | { type: 'set-template-type'; templateType: TemplateType }
  | { type: 'set-template-user-revision'; userRevision: number }
  | { type: 'add-section' }
  | { type: 'move-section-down'; sectionUuid: string; offset: number }
  | { type: 'move-section-up'; sectionUuid: string; offset: number }
  | { type: 'remove-section'; section: Section }
  | { type: 'set-section-label'; section: Section; label: string }
  | { type: 'add-field'; section: Section; fieldType: FieldType }
  | { type: 'move-field-down'; sectionUuid: string; field: Field }
  | { type: 'move-field-up'; sectionUuid: string; field: Field }
  | { type: 'remove-field'; sectionUuid: string; field: Field }
  | { type: 'set-field-label'; sectionUuid: string; field: Field; label: string }
  | { type: 'set-field-required'; sectionUuid: string; field: Field; required: boolean }
  | {
      type: 'set-field-default-value';
      sectionUuid: string;
      field: Field;
      defaultValue: string;
      isDefaultOption?: boolean;
    }
  | { type: 'add-choice'; section: Section; field: Field }
  | { type: 'remove-choice'; section: Section; field: Field; choice: Choice }
  | { type: 'set-choice-label'; section: Section; field: Field; choice: Choice; label: string }
  | { type: 'add-nested-field'; section: Section; field: Field; choice: Choice; fieldType: FieldType }
  | { type: 'toggle-multi-choice-preview'; section: Section; field: Field; choice: Choice }
  | { type: 'toggle-single-choice-preview'; section: Section; field: Field; choice: Choice }
  | { type: 'set-text-field-value-preview'; section: Section; field: Field; value: string }
  | { type: 'dev-recalibrate' }
  | { type: 'set-section-measurement-type'; sectionUuid: string; measurementType: MeasurementType }
  | { type: 'set-field-measurement-unit'; sectionUuid: string; field: Field; unit: UnitType | null };

export type State = NewTemplate;

const findChoice = (draft: State, section: Section, field: Field, choiceUuid: string): Choice | null =>
  findField(draft, section.uuid, field)?.choices_json.find((c) => c.uuid === choiceUuid) || null;

const findField = (draft: State, sectionUuid: string, field: Field): Field | null =>
  findSection(draft, sectionUuid)?.exam_fields.find((f) => f.uuid === field.uuid) || null;

const findSection = (draft: State, sectionUuid: string): Section | null =>
  draft.exam_sections.find((s) => s.uuid === sectionUuid) || null;

interface NewFieldParams {
  fieldType: FieldType;
  sequence_index: number;
  parentField?: Field;
  parentChoice?: Choice;
  unit?: UnitType | null;
}

const newField = ({ fieldType, sequence_index, parentField, parentChoice, unit }: NewFieldParams): Field => {
  const choices =
    fieldType === FieldType.SINGLE_SELECT || fieldType === FieldType.MULTI_SELECT
      ? [{ uuid: uuidv4(), label: '' }]
      : [];

  const uuid = uuidv4();

  return {
    label: '',
    uuid,
    key: uuid,
    data_type: FieldDataType.STRING,
    input_type: fieldType,
    is_required: false,
    default_value: [],
    preview_default_value: [],
    choices_json: choices,
    parent_uuid: parentField?.uuid || null,
    parent_choice_uuid: parentChoice?.uuid || null,
    sequence_index,
    subsequence_index: 0,
    ...(unit ? { measurement_unit: unit } : {}),
  };
};

const newCreditsField = (): Field => {
  const choices = [
    { uuid: uuidv4(), label: 'Credit earned' },
    { uuid: uuidv4(), label: 'Flag for review' },
  ];

  const uuid = uuidv4();

  return {
    label: '',
    uuid,
    key: uuid,
    data_type: FieldDataType.STRING,
    input_type: FieldType.MULTI_SELECT,
    is_required: false,
    default_value: [],
    preview_default_value: [],
    choices_json: choices,
    parent_uuid: null,
    parent_choice_uuid: null,
    sequence_index: 0,
    subsequence_index: 0,
  };
};

const findNextSibling = (fields: Field[], field: Field): Field | null => {
  // This function assumes that sequence_indexes match the position of the field in the array of fields
  const parentUuid = field.parent_uuid;

  for (let i = field.sequence_index + 1; i < fields.length; i++) {
    const nextField = fields[i];
    if (nextField.parent_uuid === parentUuid) {
      return nextField;
    }
  }

  return null;
};

const findPreviousSibling = (fields: Field[], field: Field): Field | null => {
  // This function assumes that sequence_indexes match the position of the field in the array of fields
  const parentUuid = field.parent_uuid;

  for (let i = field.sequence_index - 1; i >= 0; i--) {
    const prevField = fields[i];
    if (prevField.parent_uuid === parentUuid) {
      return prevField;
    }
  }

  return null;
};

const findEndIndex = (fields: Field[], field: Field, start: number, pointer: number): number => {
  if (fields.length === pointer) {
    return pointer - 1;
  } else if (field.parent_uuid === null && fields[pointer].parent_uuid === null) {
    return pointer - 1;
  } else if (field.uuid !== fields[pointer].parent_uuid) {
    return pointer - 1;
  } else {
    // current pointed is a child of the current field
    let endOfChild = start;
    while (fields.length >= endOfChild + 2 && field.uuid === fields[endOfChild + 1].parent_uuid) {
      endOfChild = findEndIndex(fields, fields[endOfChild + 1], endOfChild + 1, endOfChild + 2);
    }

    return endOfChild;
  }
};

const newSection = (sequence_index: number): Section => ({
  uuid: uuidv4(),
  label: '',
  exam_fields: [],
  sequence_index,
  subsequence_index: 0,
});

const newCreditsSection = (sequence_index: number): Section => ({
  uuid: uuidv4(),
  label: '',
  credits: true,
  exam_fields: [newCreditsField()],
  sequence_index,
  subsequence_index: 0,
});

const removeField = (field: Field, fields: Field[]): void => {
  const fieldEndIndex = findEndIndex(fields, field, field.sequence_index, field.sequence_index + 1);

  fields.splice(field.sequence_index, fieldEndIndex + 1 - field.sequence_index);

  // re-calibrate sequence_indexes an subSequenceIndexes
  fields.forEach((f, i) => {
    f.sequence_index = i;
  });
};

const toggleMultiChoicePreview = (field: Field, choice: Choice): void => {
  const choiceIndex = field.preview_default_value.indexOf(choice.uuid);
  if (choiceIndex === -1) {
    field.preview_default_value.push(choice.uuid);
  } else {
    field.preview_default_value.splice(choiceIndex, 1);
  }
};

const toggleSingleChoicePreview = (field: Field, choice: Choice): void => {
  const choiceIndex = field.preview_default_value.indexOf(choice.uuid);
  if (choiceIndex === -1) {
    field.preview_default_value = [choice.uuid];
  } else {
    field.preview_default_value = [];
  }
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'set-template-name':
      return produce(state, (draft) => {
        draft.name = action.name;
      });
    case 'set-template-reference-label':
      return produce(state, (draft) => {
        draft.reference_label = action.referenceLabel;
      });
    case 'set-template-type':
      return produce(state, (draft) => {
        draft.type = action.templateType;
        if (action.templateType === TemplateType.QA) {
          draft.exam_sections.push(newCreditsSection(draft.exam_sections.length));
        } else {
          const creditsSectionIndex = draft.exam_sections.findIndex((section) => section?.credits);
          if (creditsSectionIndex !== -1) {
            draft.exam_sections.splice(creditsSectionIndex, 1);
          }
        }
      });
    case 'set-template-user-revision':
      return produce(state, (draft) => {
        draft.user_revision = action.userRevision;
      });
    case 'add-choice':
      return produce(state, (draft) => {
        const field = findField(draft, action.section.uuid, action.field);

        if (field) field.choices_json.push({ uuid: uuidv4(), label: '' });
      });
    case 'add-section':
      return produce(state, (draft) => {
        if (draft.type === TemplateType.QA) {
          const newSectionIndex = draft.exam_sections.length - 1;
          const creditsSection = draft.exam_sections[newSectionIndex];
          draft.exam_sections.splice(newSectionIndex, 1, newSection(newSectionIndex));
          creditsSection.sequence_index = newSectionIndex + 1;
          draft.exam_sections.push(creditsSection);
        } else {
          draft.exam_sections.push(newSection(draft.exam_sections.length));
        }
      });
    case 'move-section-down':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section || (draft.type === TemplateType.QA && draft.exam_sections.length - 2 === section.sequence_index))
          return;

        const new_sequence_index = Math.min(draft.exam_sections.length - 1, section.sequence_index + action.offset);

        draft.exam_sections.splice(section.sequence_index, 1);
        draft.exam_sections.splice(new_sequence_index, 0, section);

        // re-calibrate sequence_indexes
        draft.exam_sections.forEach((s, i) => {
          s.sequence_index = i;
        });
      });
    case 'move-section-up':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section) return;

        const new_sequence_index = Math.max(
          0,
          Math.min(section.sequence_index - action.offset, draft.exam_sections.length - 1),
        );

        draft.exam_sections.splice(section.sequence_index, 1);
        draft.exam_sections.splice(new_sequence_index, 0, section);

        // re-calibrate sequence_indexes
        draft.exam_sections.forEach((s, i) => {
          s.sequence_index = i;
        });
      });
    case 'remove-section':
      return produce(state, (draft) => {
        const { exam_sections: sections } = draft;

        const index = sections.findIndex((s) => s.uuid === action.section.uuid);
        sections.splice(index, 1);

        // re-calibrate sequence_indexes
        sections.forEach((s) => {
          if (s.sequence_index > action.section.sequence_index) {
            s.sequence_index -= 1;
          }
        });
      });
    case 'set-section-label':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);

        if (section) section.label = action.label;
      });
    case 'add-field':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        const { fieldType } = action;

        if (!section) return;

        section.exam_fields.push(newField({ fieldType, sequence_index: 0 }));

        // recalibrate sequence indexes
        section.exam_fields.forEach((f, i) => {
          f.sequence_index = i;
        });
      });
    case 'move-field-down':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        const nextSibling = findNextSibling(section.exam_fields, field);
        if (!nextSibling) return;

        const nextSiblingEndIndex = findEndIndex(
          section.exam_fields,
          nextSibling,
          nextSibling.sequence_index,
          nextSibling.sequence_index + 1,
        );

        const nextRegion = section.exam_fields.splice(
          nextSibling.sequence_index,
          nextSiblingEndIndex + 1 - nextSibling.sequence_index,
        );
        section.exam_fields.splice(field.sequence_index, 0, ...nextRegion);

        // re-calibrate sequence_indexes
        section.exam_fields.forEach((f, i) => {
          f.sequence_index = i;
        });
      });
    case 'move-field-up':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        const prevSibling = findPreviousSibling(section.exam_fields, field);
        if (!prevSibling) return;

        const fieldEndIndex = findEndIndex(section.exam_fields, field, field.sequence_index, field.sequence_index + 1);

        const region = section.exam_fields.splice(field.sequence_index, fieldEndIndex + 1 - field.sequence_index);
        section.exam_fields.splice(prevSibling.sequence_index, 0, ...region);

        section.exam_fields.forEach((f, i) => {
          f.sequence_index = i;
        });
      });
    case 'remove-field':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        const { exam_fields: fields = [] } = section || {};

        const field = findField(draft, action.sectionUuid, action.field);
        if (!field) return;

        removeField(field, fields);
      });
    case 'set-field-label':
      return produce(state, (draft) => {
        const field = findField(draft, action.sectionUuid, action.field);

        if (field) field.label = action.label;
      });
    case 'set-field-required':
      return produce(state, (draft) => {
        const field = findField(draft, action.sectionUuid, action.field);

        if (field) field.is_required = action.required;
      });
    case 'set-field-default-value':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section) return;

        const field = findField(draft, action.sectionUuid, action.field);

        if (!field) return;

        if (field.input_type === FieldType.SINGLE_SELECT) {
          if (action.isDefaultOption) {
            field.default_value = [action.defaultValue];
          } else {
            field.default_value = [];
          }
        } else if (field.input_type === FieldType.MULTI_SELECT) {
          if (action.isDefaultOption) {
            field.default_value = [...(field.default_value || []), action.defaultValue];
          } else {
            field.default_value = field.default_value.filter((uuid) => uuid !== action.defaultValue);
          }
        } else {
          field.default_value = [action.defaultValue];
        }

        field.preview_default_value = field.default_value;
      });
    case 'remove-choice':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        const field = findField(draft, action.section.uuid, action.field);
        const choices = field?.choices_json || [];

        choices.splice(
          choices.findIndex((choice) => choice.uuid === action.choice.uuid),
          1,
        );

        // remove field if the field has a parent field and there are no more choices
        if (action.field.parent_uuid && choices.length === 0) {
          if (section && field) {
            removeField(field, section.exam_fields);
          }
        }

        // remove all of the child fields of this option
        if (section) {
          const choiceFields = section.exam_fields.filter((f) => f.parent_choice_uuid === action.choice.uuid);

          choiceFields.forEach((choiceField) => removeField(choiceField, section.exam_fields));
        }

        if (field && field.default_value.includes(action.choice.uuid)) {
          field.default_value = field.default_value.filter((uuid) => uuid !== action.choice.uuid);
        }
      });
    case 'set-choice-label':
      return produce(state, (draft) => {
        const choice = findChoice(draft, action.section, action.field, action.choice.uuid);

        if (choice) choice.label = action.label;
      });
    case 'add-nested-field':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        const fieldEndIndex = findEndIndex(section.exam_fields, field, field.sequence_index, field.sequence_index + 1);

        const sequence_index = action.field.sequence_index + 1;
        const theNewField = newField({
          fieldType: action.fieldType,
          sequence_index,
          parentField: action.field,
          parentChoice: action.choice,
        });

        section.exam_fields.splice(fieldEndIndex + 1, 0, theNewField);
        // recalibrate sequence indexes
        section.exam_fields.forEach((f, i) => {
          f.sequence_index = i;
        });
      });
    case 'set-text-field-value-preview':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        field.preview_default_value = [action.value];
      });
    case 'toggle-multi-choice-preview':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        const choice = findChoice(draft, section, field, action.choice.uuid);
        if (!choice) return;

        toggleMultiChoicePreview(field, choice);
      });
    case 'toggle-single-choice-preview':
      return produce(state, (draft) => {
        const section = findSection(draft, action.section.uuid);
        if (!section) return;

        const field = findField(draft, section.uuid, action.field);
        if (!field) return;

        const choice = findChoice(draft, section, field, action.choice.uuid);
        if (!choice) return;

        toggleSingleChoicePreview(field, choice);
      });
    case 'dev-recalibrate':
      return produce(state, (draft) => {
        draft.exam_sections.forEach((s) => {
          s.exam_fields.sort((a, b) => {
            if (a.sequence_index !== b.sequence_index) {
              return a.sequence_index - b.sequence_index;
            }
            return a.subsequence_index - b.subsequence_index;
          });
          s.exam_fields.forEach((f, i) => {
            f.sequence_index = i;
            f.subsequence_index = 0;
          });
        });
      });
    case 'set-section-measurement-type':
      return produce(state, (draft) => {
        const section = findSection(draft, action.sectionUuid);
        if (!section) return;

        section.measurement_type = action.measurementType;

        section.exam_fields = [];

        switch (action.measurementType) {
          case MeasurementType.LINEAR:
            section.exam_fields.push(newField({ fieldType: FieldType.NUMERIC, sequence_index: 0, unit: null }));
            break;
          case MeasurementType.VTI:
            Array.from(Array(3).keys()).forEach((x) => {
              section.exam_fields.push(newField({ fieldType: FieldType.NUMERIC, sequence_index: x, unit: null }));
            });
            break;
          case MeasurementType.ELLIPTICAL:
            section.exam_fields.push(newField({ fieldType: FieldType.NUMERIC, sequence_index: 0, unit: null }));
            break;
        }
      });
    case 'set-field-measurement-unit':
      return produce(state, (draft) => {
        const field = findField(draft, action.sectionUuid, action.field);
        if (field) field.measurement_unit = action.unit;
      });
    default:
      return state;
  }
};

export { reducer };
